import type { PathwayId } from '@ircf/config';

/**
 * Scene chapters.
 *
 * A chapter is a named stretch of a public route's scroll narrative. Pages mark
 * their sections with `data-scene-chapter="<id>"`; the canvas observes those
 * elements and derives which chapter is active and how far through it the
 * visitor is.
 *
 * Deriving chapter state from the DOM rather than from hardcoded scroll
 * percentages is what keeps the 3D and the HTML honest: if a copywriter adds two
 * paragraphs, the narrative stretches with the content instead of desynchronising.
 */

export const MASTER_CHAPTERS = [
  'arrival',
  'coach',
  'method',
  'proof',
  'invitation',
  'pathways',
] as const;

export const PT_CHAPTERS = [
  'blueprint',
  'coach',
  'goals',
  'nutrition',
  'strength',
  'cardio',
  'blueprint-calendar',
  'scan',
  'plan',
] as const;

export const BOXING_CHAPTERS = [
  'walkout',
  'corner',
  'programs',
  'heavybag',
  'scorecard',
  'final-round',
] as const;

export type MasterChapter = (typeof MASTER_CHAPTERS)[number];
export type PtChapter = (typeof PT_CHAPTERS)[number];
export type BoxingChapter = (typeof BOXING_CHAPTERS)[number];

export type SceneChapter = MasterChapter | PtChapter | BoxingChapter;

export const CHAPTERS_BY_PATHWAY: Record<PathwayId, readonly SceneChapter[]> = {
  master: MASTER_CHAPTERS,
  'personal-training': PT_CHAPTERS,
  boxing: BOXING_CHAPTERS,
};

/** Round labels for the boxing experience, driven by the active chapter. */
export const BOXING_ROUND_LABELS: Record<BoxingChapter, string> = {
  walkout: 'The Walkout',
  corner: 'Round 01',
  programs: 'Round 02',
  heavybag: 'Round 03',
  scorecard: 'Round 04',
  'final-round': 'Your Round',
};

/** The five method words revealed as the master Performance Axis opens. */
export const METHOD_WORDS = ['Assess', 'Plan', 'Coach', 'Measure', 'Progress'] as const;

/** The four words revealed as the Performance Core separates. */
export const CORE_WORDS = ['Fuel', 'Build', 'Condition', 'Adapt'] as const;

export function isSceneChapter(value: string): value is SceneChapter {
  return (
    (MASTER_CHAPTERS as readonly string[]).includes(value) ||
    (PT_CHAPTERS as readonly string[]).includes(value) ||
    (BOXING_CHAPTERS as readonly string[]).includes(value)
  );
}
